import React, { useEffect, useState } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCryptoPrices } from '@/shared/hooks/useCryptoPrices';
import { Clock, CheckCircle2, XCircle, AlertCircle, User, ArrowDownLeft, ArrowUpRight, Trash2 } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { formatDisplayName, maskString } from '@/shared/utils/formatters';

interface TransactionRequest {
    id: string;
    userId: string;
    userName: string;
    type: 'deposit' | 'withdrawal';
    currency: string;
    amount: number;
    status: 'pending' | 'account_issued' | 'deposit_claimed' | 'approved' | 'rejected';

    // KRW deposit specific (legacy support)
    virtualAccount?: {
        bank: string;
        accountNumber: string;
        accountHolder: string;
    };

    // KRW withdrawal specific
    withdrawalBankInfo?: {
        bank: string;
        accountNumber: string;
        accountHolder: string;
    };

    // Crypto deposit specific
    depositAddress?: string;

    // Crypto withdrawal specific
    withdrawalAddress?: string;

    rejectionReason?: string;
    createdAt: any;
    updatedAt: any;
    processedAt?: any;
}

const AdminTransactionRequestsPage: React.FC = () => {
    const [requests, setRequests] = useState<TransactionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('All');
    const [currencyFilter, setCurrencyFilter] = useState('All');
    const { prices: cryptoPrices } = useCryptoPrices();

    // Modal States
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showVirtualAccountModal, setShowVirtualAccountModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<TransactionRequest | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');

    // Virtual Account States
    const [vaBank, setVaBank] = useState('국민은행');
    const [vaAccountNumber, setVaAccountNumber] = useState('');
    const [vaAccountHolder, setVaAccountHolder] = useState('');

    useEffect(() => {
        // Set timeout to prevent infinite loading
        const loadingTimeout = setTimeout(() => {
            setLoading(false);
        }, 10000); // 10 second timeout

        try {
            const q = query(collection(db, 'transactionRequests'));

            const unsubscribe = onSnapshot(
                q,
                (snapshot) => {
                    const data = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })) as TransactionRequest[];

                    // Sort by latest
                    data.sort((a, b) => {
                        const aTime = a.createdAt?.toMillis?.() || 0;
                        const bTime = b.createdAt?.toMillis?.() || 0;
                        return bTime - aTime;
                    });

                    setRequests(data);
                    setLoading(false);
                    clearTimeout(loadingTimeout);
                },
                (error) => {
                    console.error('Error fetching transaction requests:', error);
                    setLoading(false);
                    clearTimeout(loadingTimeout);
                    alert('데이터를 불러오는 중 오류가 발생했습니다.');
                }
            );

            return () => {
                unsubscribe();
                clearTimeout(loadingTimeout);
            };
        } catch (error) {
            console.error('Error setting up listener:', error);
            setLoading(false);
            clearTimeout(loadingTimeout);
        }
    }, []);

    const handleApprove = async (request: TransactionRequest) => {
        const action = request.type === 'deposit' ? '입금' : '출금';
        const currencyDisplay = request.currency === 'KRW'
            ? `₩${request.amount.toLocaleString()}`
            : `${request.amount} ${request.currency}`;

        // Add current market price for crypto withdrawals
        let priceInfo = '';
        if (request.type === 'withdrawal' && ['BTC', 'ETH', 'USDT'].includes(request.currency)) {
            const currentPrice = cryptoPrices[request.currency]?.price || 0;
            const totalKRW = request.amount * currentPrice;
            priceInfo = `\n현재 시세: ₩${currentPrice.toLocaleString()} (총 ₩${Math.round(totalKRW).toLocaleString()})`;
        }

        if (!confirm(`${request.userName}님의 ${currencyDisplay} ${action}을 승인하시겠습니까?${priceInfo}`)) {
            return;
        }

        try {
            const userRef = doc(db, 'users', request.userId);
            const assetKey = request.currency.toLowerCase();
            const amount = Number(request.amount.toString().replace(/[^0-9.-]/g, ''));

            if (isNaN(amount)) {
                console.error("Invalid amount detected:", request.amount);
                return;
            }

            if (request.type === 'deposit') {
                // Increase balance (Update both root and nested for compatibility)
                await updateDoc(userRef, {
                    [assetKey]: increment(amount),
                    [`assets.${assetKey}`]: increment(amount)
                });
            } else {
                // Decrease balance
                await updateDoc(userRef, {
                    [assetKey]: increment(-amount),
                    [`assets.${assetKey}`]: increment(-amount)
                });
            }

            // Update request status
            await updateDoc(doc(db, 'transactionRequests', request.id), {
                status: 'approved',
                processedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            alert('승인이 완료되었습니다.');
        } catch (error) {
            console.error('승인 처리 실패:', error);
            alert('승인 중 오류가 발생했습니다.');
        }
    };

    const handleReject = async () => {
        if (!selectedRequest || !rejectionReason.trim()) {
            alert('거절 사유를 입력해주세요.');
            return;
        }

        try {
            await updateDoc(doc(db, 'transactionRequests', selectedRequest.id), {
                status: 'rejected',
                rejectionReason: rejectionReason,
                processedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            setShowRejectModal(false);
            setSelectedRequest(null);
            setRejectionReason('');
            alert('거절 처리가 완료되었습니다.');
        } catch (error) {
            console.error('거절 처리 실패:', error);
            alert('거절 중 오류가 발생했습니다.');
        }
    };

    const handleIssueVirtualAccount = async () => {
        if (!selectedRequest) return;

        if (!vaAccountNumber.trim() || !vaAccountHolder.trim()) {
            alert('계좌 정보를 모두 입력해주세요.');
            return;
        }

        try {
            await updateDoc(doc(db, 'transactionRequests', selectedRequest.id), {
                status: 'account_issued',
                virtualAccount: {
                    bank: vaBank,
                    accountNumber: vaAccountNumber,
                    accountHolder: vaAccountHolder
                },
                updatedAt: serverTimestamp()
            });

            setShowVirtualAccountModal(false);
            setSelectedRequest(null);
            setVaBank('국민은행');
            setVaAccountNumber('');
            setVaAccountHolder('');
            alert('가상계좌가 발급되었습니다.');
        } catch (error) {
            console.error('가상계좌 발급 실패:', error);
            alert('가상계좌 발급 중 오류가 발생했습니다.');
        }
    };

    const filteredRequests = requests.filter(r => {
        const typeMatch = typeFilter === 'All' || r.type === typeFilter.toLowerCase();
        const currencyMatch = currencyFilter === 'All' || r.currency === currencyFilter;
        return typeMatch && currencyMatch;
    });

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-500 text-xs font-black uppercase">계좌 발급 대기</span>;
            case 'account_issued':
                return <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-500 text-xs font-black uppercase">계좌 발급됨</span>;
            case 'deposit_claimed':
                return <span className="px-3 py-1 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-500 text-xs font-black uppercase">입금 완료 대기</span>;
            case 'approved':
                return <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 text-xs font-black uppercase">승인 완료</span>;
            case 'rejected':
                return <span className="px-3 py-1 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-500 text-xs font-black uppercase">거절됨</span>;
            default:
                return <span className="px-3 py-1 rounded-full bg-gray-500/10 text-gray-600 text-xs font-black uppercase">{status}</span>;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24 text-foreground">
            <div className="max-w-7xl mx-auto px-4 lg:px-8 space-y-8 pt-8">
                {/* Header */}
                <div className="space-y-4">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">관리자 전용</span>
                    </div>
                    <h1 className="text-4xl font-black tracking-tighter uppercase leading-tight">
                        입출금 신청 관리
                        <br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500">
                            실시간 입출금 요청
                        </span>
                    </h1>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-4">
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        <span className="text-xs font-black text-muted-foreground uppercase tracking-widest self-center whitespace-nowrap">타입:</span>
                        {['All', 'Deposit', 'Withdrawal'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setTypeFilter(f)}
                                className={cn(
                                    "px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all",
                                    typeFilter === f
                                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                                        : "bg-card border border-border text-muted-foreground hover:bg-accent"
                                )}
                            >
                                {f === 'Deposit' ? '입금' : f === 'Withdrawal' ? '출금' : f}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        <span className="text-xs font-black text-muted-foreground uppercase tracking-widest self-center whitespace-nowrap">자산:</span>
                        {['All', 'KRW', 'BTC', 'ETH', 'USDT'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setCurrencyFilter(f)}
                                className={cn(
                                    "px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all",
                                    currencyFilter === f
                                        ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                                        : "bg-card border border-border text-muted-foreground hover:bg-accent"
                                )}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Requests Table */}
                <div className="bg-card border border-border rounded-[2rem] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-accent/30 border-b border-border">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">타입</th>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">사용자</th>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">자산</th>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">금액</th>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">상태</th>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">신청일시</th>
                                    <th className="px-6 py-4 text-left text-xs font-black text-muted-foreground uppercase tracking-widest">작업</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredRequests.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-16 text-center text-muted-foreground font-bold">
                                            신청 내역이 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRequests.map((request) => {
                                        const TypeIcon = request.type === 'deposit' ? ArrowDownLeft : ArrowUpRight;
                                        const typeColor = request.type === 'deposit' ? 'text-blue-600' : 'text-rose-600';
                                        const typeBg = request.type === 'deposit' ? 'bg-blue-500/10' : 'bg-rose-500/10';

                                        return (
                                            <tr key={request.id} className="hover:bg-accent/30 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className={cn("flex items-center justify-center w-10 h-10 rounded-full", typeBg)}>
                                                        <TypeIcon className={cn("h-5 w-5", typeColor)} />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                                                            <User className="h-5 w-5 text-muted-foreground" />
                                                        </div>
                                                        <div>
                                                            <p className="font-black">{formatDisplayName({ userName: request.userName, id: request.userId })}</p>
                                                            <p className="text-xs text-muted-foreground font-bold">{maskString(request.userId)}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-3 py-1 rounded-full bg-accent border border-border font-black text-xs">
                                                        {request.currency}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="font-black text-lg tabular-nums">
                                                        {request.currency === 'KRW'
                                                            ? `₩${request.amount.toLocaleString()}`
                                                            : `${request.amount} ${request.currency}`
                                                        }
                                                    </p>
                                                    {['BTC', 'ETH', 'USDT'].includes(request.currency) && (
                                                        <p className="text-xs text-blue-500 font-bold tabular-nums mt-0.5">
                                                            ≈ ₩{Math.round(request.amount * (cryptoPrices[request.currency]?.price || 0)).toLocaleString()}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {getStatusBadge(request.status)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-bold text-muted-foreground">
                                                        {request.createdAt?.toDate?.().toLocaleString('ko-KR')}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-2">
                                                        {/* 원화 입금 pending - 계좌 발급 버튼 */}
                                                        {request.type === 'deposit' && request.currency === 'KRW' && request.status === 'pending' && (
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedRequest(request);
                                                                    setShowVirtualAccountModal(true);
                                                                }}
                                                                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-black uppercase hover:scale-105 active:scale-95 transition-all"
                                                            >
                                                                계좌 발급
                                                            </button>
                                                        )}

                                                        {/* 원화 입금 deposit_claimed - 승인/거절 버튼 */}
                                                        {request.type === 'deposit' && request.currency === 'KRW' && request.status === 'deposit_claimed' && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleApprove(request)}
                                                                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-black uppercase hover:scale-105 active:scale-95 transition-all"
                                                                >
                                                                    승인
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedRequest(request);
                                                                        setShowRejectModal(true);
                                                                    }}
                                                                    className="px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-black uppercase hover:scale-105 active:scale-95 transition-all"
                                                                >
                                                                    거절
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* 다른 모든 pending (코인 입금, 원화/코인 출금) - 승인/거절 버튼 */}
                                                        {!(request.type === 'deposit' && request.currency === 'KRW') && request.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleApprove(request)}
                                                                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-black uppercase hover:scale-105 active:scale-95 transition-all"
                                                                >
                                                                    승인
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedRequest(request);
                                                                        setShowRejectModal(true);
                                                                    }}
                                                                    className="px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-black uppercase hover:scale-105 active:scale-95 transition-all"
                                                                >
                                                                    거절
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* account_issued 상태 - 대기 중 */}
                                                        {request.status === 'account_issued' && (
                                                            <span className="text-xs text-muted-foreground font-bold">사용자 입금 대기 중</span>
                                                        )}

                                                        {/* approved, rejected - 처리 완료 */}
                                                        {(request.status === 'approved' || request.status === 'rejected') && (
                                                            <span className="text-xs text-muted-foreground font-bold">처리 완료</span>
                                                        )}

                                                        {/* Delete Button - Always visible for admins */}
                                                        <button
                                                            onClick={async () => {
                                                                if (confirm('이 신청 내역을 완전히 삭제하시겠습니까?\n삭제된 내역은 복구할 수 없으며 유저 페이지에서도 사라집니다.')) {
                                                                    try {
                                                                        await deleteDoc(doc(db, 'transactionRequests', request.id));
                                                                        alert('삭제되었습니다.');
                                                                    } catch (error) {
                                                                        console.error('삭제 실패:', error);
                                                                        alert('삭제 중 오류가 발생했습니다.');
                                                                    }
                                                                }
                                                            }}
                                                            className="p-2 rounded-lg bg-card border border-border text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                                                            title="내역 삭제"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Reject Modal */}
            {showRejectModal && selectedRequest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="w-full max-w-md bg-card rounded-[2rem] border border-border shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-border">
                            <h3 className="text-lg font-black uppercase tracking-tight">
                                {selectedRequest.type === 'deposit' ? '입금' : '출금'} 거절
                            </h3>
                            <p className="text-sm text-muted-foreground font-bold mt-1">
                                {formatDisplayName({ userName: selectedRequest.userName, id: selectedRequest.userId })}님 · {selectedRequest.currency === 'KRW'
                                    ? `₩${selectedRequest.amount.toLocaleString()}`
                                    : `${selectedRequest.amount} ${selectedRequest.currency}`
                                }
                            </p>
                        </div>
                        <div className="p-8 space-y-4">
                            <div className="space-y-2">
                                <label className="text-xs font-black text-muted-foreground uppercase tracking-widest">거절 사유</label>
                                <textarea
                                    placeholder="거절 사유를 입력해주세요."
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold resize-none"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-border flex gap-3">
                            <button
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setSelectedRequest(null);
                                    setRejectionReason('');
                                }}
                                className="flex-1 py-3 rounded-xl bg-card border border-border text-foreground font-black uppercase hover:bg-accent transition-colors"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleReject}
                                className="flex-1 py-3 rounded-xl bg-rose-600 text-white font-black uppercase hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-rose-600/20"
                            >
                                거절하기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Virtual Account Modal */}
            {showVirtualAccountModal && selectedRequest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="w-full max-w-md bg-card rounded-[2rem] border border-border shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-border">
                            <h3 className="text-lg font-black uppercase tracking-tight">
                                가상계좌 발급
                            </h3>
                            <p className="text-sm text-muted-foreground font-bold mt-1">
                                {formatDisplayName({ userName: selectedRequest.userName, id: selectedRequest.userId })}님 · ₩{selectedRequest.amount.toLocaleString()}
                            </p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-xs font-black text-muted-foreground uppercase tracking-widest">은행</label>
                                <select
                                    value={vaBank}
                                    onChange={(e) => setVaBank(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-blue-500 font-bold"
                                >
                                    <option value="국민은행">국민은행</option>
                                    <option value="신한은행">신한은행</option>
                                    <option value="우리은행">우리은행</option>
                                    <option value="하나은행">하나은행</option>
                                    <option value="농협은행">농협은행</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-black text-muted-foreground uppercase tracking-widest">계좌번호</label>
                                <input
                                    type="text"
                                    placeholder="계좌번호를 입력하세요"
                                    value={vaAccountNumber}
                                    onChange={(e) => setVaAccountNumber(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-blue-500 font-bold"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-black text-muted-foreground uppercase tracking-widest">예금주</label>
                                <input
                                    type="text"
                                    placeholder="예금주명을 입력하세요"
                                    value={vaAccountHolder}
                                    onChange={(e) => setVaAccountHolder(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-blue-500 font-bold"
                                />
                            </div>
                        </div>

                        <div className="p-6 border-t border-border flex gap-3">
                            <button
                                onClick={() => {
                                    setShowVirtualAccountModal(false);
                                    setSelectedRequest(null);
                                    setVaBank('국민은행');
                                    setVaAccountNumber('');
                                    setVaAccountHolder('');
                                }}
                                className="flex-1 py-3 rounded-xl bg-card border border-border text-foreground font-black uppercase hover:bg-accent transition-colors"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleIssueVirtualAccount}
                                className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-black uppercase hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-blue-600/20"
                            >
                                발급하기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTransactionRequestsPage;
